import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { formatMoney, currencyCode } from '../utils/currency';

function AddForm({ fields, initial = {}, onSubmit, onCancel, submitLabel = 'Save' }) {
  const initialForm = () => Object.fromEntries(fields.map(f => [f.key, initial[f.key] ?? f.default ?? '']));
  const [form, setForm] = useState(initialForm);

  useEffect(() => { setForm(initialForm()); }, [initial?.id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="inline-form mt-3">
      <div className="form-grid form-grid-2">
        {fields.map(f => (
          <div className="field" key={f.key} style={f.full ? { gridColumn: '1 / -1' } : {}}>
            <label>{f.label}</label>
            {f.type === 'select' ? (
              <select value={form[f.key]} onChange={e => set(f.key, e.target.value)}>
                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea value={form[f.key]} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} />
            ) : (
              <input type={f.type || 'text'} value={form[f.key]} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} />
            )}
          </div>
        ))}
      </div>
      <div className="form-actions mt-3">
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={() => onSubmit(form)}>{submitLabel}</button>
      </div>
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

export default function HealthTab({ sheepId }) {
  const [vaccinations, setVaccinations] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [events, setEvents] = useState([]);
  const [showVaccForm, setShowVaccForm] = useState(false);
  const [showTreatForm, setShowTreatForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingVacc, setEditingVacc] = useState(null);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [error, setError] = useState('');

  const load = () => {
    setError('');
    Promise.all([
      api.listVaccinations(sheepId),
      api.listTreatments(sheepId),
      api.listHealthEvents(sheepId),
    ])
      .then(([vaccs, treats, evts]) => {
        setVaccinations(vaccs);
        setTreatments(treats);
        setEvents(evts);
      })
      .catch(err => setError(err.message));
  };

  useEffect(() => { load(); }, [sheepId]);

  const saveVaccination = async (data) => {
    try {
      editingVacc
        ? await api.updateVaccination(editingVacc.id, data)
        : await api.addVaccination(sheepId, data);
      setShowVaccForm(false);
      setEditingVacc(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveTreatment = async (data) => {
    try {
      editingTreatment
        ? await api.updateTreatment(editingTreatment.id, data)
        : await api.addTreatment(sheepId, data);
      setShowTreatForm(false);
      setEditingTreatment(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveEvent = async (data) => {
    try {
      const payload = { ...data, resolved: data.resolved === '1' ? 1 : 0 };
      editingEvent
        ? await api.replaceHealthEvent(editingEvent.id, payload)
        : await api.addHealthEvent(sheepId, payload);
      setShowEventForm(false);
      setEditingEvent(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (fn) => {
    try {
      await fn();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const vaccFields = [
    { key: 'vaccine_type', label: 'Vaccine Type', placeholder: 'e.g. Clostridial 7-in-1' },
    { key: 'date_given', label: 'Date Given', type: 'date', default: today() },
    { key: 'due_date', label: 'Next Due Date', type: 'date' },
    { key: 'cost', label: `Cost (${currencyCode()})`, type: 'number' },
    { key: 'notes', label: 'Notes', type: 'textarea', full: true },
  ];

  const treatmentFields = [
    { key: 'condition', label: 'Condition', placeholder: 'e.g. Footrot' },
    { key: 'drug', label: 'Drug / Product', placeholder: 'e.g. Oxytetracycline' },
    { key: 'dose', label: 'Dose', placeholder: 'e.g. 5ml' },
    { key: 'date_given', label: 'Date', type: 'date', default: today() },
    { key: 'cost', label: `Cost (${currencyCode()})`, type: 'number' },
    { key: 'notes', label: 'Notes', type: 'textarea', full: true },
  ];

  const eventFields = [
    { key: 'event_type', label: 'Type', type: 'select', default: 'illness', options: [{ value: 'illness', label: 'Illness' }, { value: 'injury', label: 'Injury' }] },
    { key: 'date', label: 'Date', type: 'date', default: today() },
    { key: 'resolved', label: 'Status', type: 'select', default: '0', options: [{ value: '0', label: 'Open' }, { value: '1', label: 'Resolved' }] },
    { key: 'description', label: 'Description', placeholder: 'Describe the issue', full: true },
    { key: 'notes', label: 'Notes', type: 'textarea', full: true },
  ];

  return (
    <div>
      {error && <div className="alert alert-danger mb-4">{error}</div>}

      <div className="section-header">
        <span className="section-title">💉 Vaccinations</span>
        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingVacc(null); setShowVaccForm(v => !v); }}>+ Add</button>
      </div>

      {(showVaccForm || editingVacc) && (
        <AddForm
          fields={vaccFields}
          initial={editingVacc || {}}
          onSubmit={saveVaccination}
          onCancel={() => { setShowVaccForm(false); setEditingVacc(null); }}
          submitLabel={editingVacc ? 'Save Changes' : 'Save'}
        />
      )}

      {vaccinations.length === 0 && !showVaccForm && !editingVacc ? (
        <p className="text-muted mt-2">No vaccinations recorded.</p>
      ) : (
        <div className="table-wrap mt-2">
          <table>
            <thead><tr><th>Vaccine</th><th>Date Given</th><th>Due Date</th><th>Cost</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              {vaccinations.map(v => {
                const isOverdue = v.due_date && v.due_date < today();
                return (
                  <tr key={v.id}>
                    <td data-label="Vaccine">{v.vaccine_type}</td>
                    <td data-label="Date Given">{v.date_given}</td>
                    <td data-label="Due Date" style={isOverdue ? { color: 'var(--rust)', fontWeight: 600 } : {}}>
                      {v.due_date || '—'}{isOverdue ? ' ⚠' : ''}
                    </td>
                    <td data-label="Cost">{v.cost != null ? formatMoney(v.cost) : '—'}</td>
                    <td data-label="Notes" className="text-muted">{v.notes || '—'}</td>
                    <td data-label="Actions">
                      <div className="row-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingVacc(v); setShowVaccForm(false); }}>Edit</button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => window.confirm('Delete this vaccination record?') && remove(() => api.deleteVaccination(v.id))}>✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="divider" />

      <div className="section-header">
        <span className="section-title">💊 Treatments</span>
        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingTreatment(null); setShowTreatForm(v => !v); }}>+ Add</button>
      </div>

      {(showTreatForm || editingTreatment) && (
        <AddForm
          fields={treatmentFields}
          initial={editingTreatment || {}}
          onSubmit={saveTreatment}
          onCancel={() => { setShowTreatForm(false); setEditingTreatment(null); }}
          submitLabel={editingTreatment ? 'Save Changes' : 'Save'}
        />
      )}

      {treatments.length === 0 && !showTreatForm && !editingTreatment ? (
        <p className="text-muted mt-2">No treatments recorded.</p>
      ) : (
        <div className="table-wrap mt-2">
          <table>
            <thead><tr><th>Condition</th><th>Drug</th><th>Dose</th><th>Date</th><th>Cost</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              {treatments.map(t => (
                <tr key={t.id}>
                  <td data-label="Condition">{t.condition}</td>
                  <td data-label="Drug">{t.drug}</td>
                  <td data-label="Dose">{t.dose || '—'}</td>
                  <td data-label="Date">{t.date_given}</td>
                  <td data-label="Cost">{t.cost != null ? formatMoney(t.cost) : '—'}</td>
                  <td data-label="Notes" className="text-muted">{t.notes || '—'}</td>
                  <td data-label="Actions">
                    <div className="row-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditingTreatment(t); setShowTreatForm(false); }}>Edit</button>
                      <button className="btn btn-danger btn-sm btn-icon" onClick={() => window.confirm('Delete this treatment record?') && remove(() => api.deleteTreatment(t.id))}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="divider" />

      <div className="section-header">
        <span className="section-title">🩺 Illnesses & Injuries</span>
        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingEvent(null); setShowEventForm(v => !v); }}>+ Add</button>
      </div>

      {(showEventForm || editingEvent) && (
        <AddForm
          fields={eventFields}
          initial={editingEvent ? { ...editingEvent, resolved: String(editingEvent.resolved || 0) } : {}}
          onSubmit={saveEvent}
          onCancel={() => { setShowEventForm(false); setEditingEvent(null); }}
          submitLabel={editingEvent ? 'Save Changes' : 'Save'}
        />
      )}

      {events.length === 0 && !showEventForm && !editingEvent ? (
        <p className="text-muted mt-2">No health events recorded.</p>
      ) : (
        <div className="table-wrap mt-2">
          <table>
            <thead><tr><th>Type</th><th>Date</th><th>Description</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {events.map(e => (
                <tr key={e.id}>
                  <td data-label="Type" style={{ textTransform: 'capitalize' }}>{e.event_type}</td>
                  <td data-label="Date">{e.date}</td>
                  <td data-label="Description">{e.description}</td>
                  <td data-label="Status">
                    {e.resolved
                      ? <span className="badge badge-active">Resolved</span>
                      : <button className="btn btn-sm btn-secondary" onClick={() => remove(() => api.updateHealthEvent(e.id, { resolved: 1 }))}>Mark Resolved</button>
                    }
                  </td>
                  <td data-label="Actions">
                    <div className="row-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditingEvent(e); setShowEventForm(false); }}>Edit</button>
                      <button className="btn btn-danger btn-sm btn-icon" onClick={() => window.confirm('Delete this health event?') && remove(() => api.deleteHealthEvent(e.id))}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
